<template>
  <div
    class="product__gallery"
    :class="{ 'initial-image-png' : hasColouredBgOption }"
    :aria-busy="horizontalGalleryBusy ? 'true' : null"
  >
    <div
      class="product__gallery-skeleton"
      aria-hidden="true"
    >
      <div class="product__gallery-skeleton__main" />
      <div class="product__gallery-skeleton__thumbs">
        <span class="product__gallery-skeleton__thumb" />
        <span class="product__gallery-skeleton__thumb" />
        <span class="product__gallery-skeleton__thumb" />
        <span class="product__gallery-skeleton__thumb" />
        <span class="product__gallery-skeleton__thumb" />
        <span class="product__gallery-skeleton__thumb" />
      </div>
    </div>
    <div class="product__gallery-inner">
      <ProductSaleBadge />

      <!-- Main image slider first, thumbnails row below -->
      <vue-flickity
        :key="`main-${product.id}`"
        ref="mainCarousel"
        class="product__gallery-main-image-slider"
        :options="{
          prevNextButtons: false,
          pageDots: true,
          lazyLoad: 2,
          cellAlign: 'left',
          wrapAround: true,
        }"
      >
        <div
          v-for="(image, index) in largeMedia"
          :key="`main-image-${image}`"
          class="product__gallery-main-image"
          :data-elite-points="index === 0 ? pointsText : null"
          :data-promo-badge="index === 0 ? promoBadge : null"
        >
          <template v-if="index === 0">
            <img
              v-if="image.media_type === 'image'"
              :src="image.preview_image.src"
              :alt="`image-${index}`"
              width="500"
              height="500"
              loading="eager"
              class="hidden md:block"
            >
            <img
              v-if="image.media_type === 'image'"
              :src="image.preview_image.mobileSrc"
              :alt="`image-${index}`"
              width="288"
              height="271"
              loading="eager"
              class="md:hidden"
            >
          </template>
          <template v-else>
            <img
              v-if="image.media_type === 'image'"
              :data-flickity-lazyload="image.preview_image.src"
              :alt="`image-${index}`"
              width="500"
              height="500"
              class="hidden md:block"
            >
            <img
              v-if="image.media_type === 'image'"
              :src="image.preview_image.mobileSrc"
              :alt="`image-${index}`"
              width="288"
              height="271"
              class="md:hidden"
            >
          </template>

          <video
            v-if="image.media_type === 'video'"
            width="500"
            height="500"
            controls
          >
            <source
              v-for="source in image.sources"
              :key="source.format"
              :src="source.url"
              :type="source.mime_type"
            >
            Your browser does not support the video tag.
          </video>
        </div>
      </vue-flickity>

      <vue-flickity
        :key="`thumbs-${product.id}`"
        ref="thumbnailCarousel"
        class="product__gallery-thumbnails"
        :options="{
          prevNextButtons: false,
          pageDots: false,
          setGallerySize: false,
          contain: true,
          cellSelector: '.product__gallery-thumbnail-wrapper',
          cellAlign: 'left',
          wrapAround: true,
          draggable: true,
          selectedAttraction: 0
        }"
      >
        <div
          v-for="(image, index) in thumbnailImages"
          :key="`thumbnail-${image}`"
          class="product__gallery-thumbnail-wrapper"
          :class="{ 'is-selected': currentSlideIndex === index }"
        >
          <div
            class="product__gallery-thumbnail"
            :class="{ 'product__gallery-thumbnail--png': image.includes('.png') }"
            @click="goToSlide(index)"
          >
            <img
              :src="image"
              :alt="`thumbnail-image-${index}`"
              width="123"
              height="123"
            >
          </div>
        </div>

        <button
          class="product__gallery-button product__gallery-button--prev"
          :class="{ 'product__gallery-button--disabled': atFirstSlide }"
          :disabled="atFirstSlide"
          aria-label="Previous Slide"
          @click="previousSlide"
        >
          <ArrowLeft />
        </button>
        <button
          class="product__gallery-button product__gallery-button--next"
          :class="{ 'product__gallery-button--disabled': atLastSlide }"
          :disabled="atLastSlide"
          aria-label="Next Slide"
          @click="nextSlide"
        >
          <ArrowLeft />
        </button>
      </vue-flickity>
    </div>
  </div>
</template>


<script>
import { mapState } from 'pinia'
import { useProductStore } from '../../pinia/ProductStore'
import {productPointsValue} from '../../helpers/product'

import ProductSaleBadge from './product-sale-badge'
import ArrowLeft from '../../icons/arrow-left'

export default {
  components: {
    ArrowLeft,
    ProductSaleBadge
  },
  props: {
  },
  data () {
    return {
      atFirstSlide: true,
      atLastSlide: false,
      currentSlideIndex: 0,
      isHorizontalThumbs: true,
      /** First paint: wait for window load before revealing horizontal Flickity */
      horizontalGalleryFirstReveal: true,
      horizontalRevealOnLoadAttached: false,
      /** Shown while horizontal gallery is in loading state (skeleton + aria-busy) */
      horizontalGalleryBusy: false
    }
  },
  computed: {
    ...mapState (useProductStore, [
      'product',
      'gradientBackgroundColours',
      'hasColouredBgOption',
      'thumbnailImages',
      'largeMedia'
    ]),
    gallerySize () {
      return this.largeMedia.length
    },
    promoBadge () {
      const badgeTag = this.product.tags.find(tag => tag.includes('promo-badge:'))
      if (badgeTag) {
        return badgeTag.split(':')[1]
      }
      return null
    },
    pointsText() {
      return productPointsValue(this.product.price)
    }
  },
  watch: {
    async product () {
      if (!this.isHorizontalThumbs && this.$refs.thumbnailCarousel) {
        await this.$refs.thumbnailCarousel.select(0)
      }
      if (this.$refs.mainCarousel) {
        await this.$refs.mainCarousel.select(0)
      }
      this.currentSlideIndex = 0
      this.atFirstSlide = true
      this.atLastSlide = false
      this.galleryLoad()
    },
  }, 
  mounted () {
    this.horizontalGalleryBusy = this.isHorizontalThumbs
    this.galleryLoad()
    if (this.isHorizontalThumbs) {
      this.bindHorizontalGalleryLayoutFix()
    }
  },
  beforeUnmount () {
    if (this._horizontalGalleryRoRafId != null) {
      cancelAnimationFrame(this._horizontalGalleryRoRafId)
      this._horizontalGalleryRoRafId = null
    }
    if (this._horizontalGalleryResizeObserver) {
      this._horizontalGalleryResizeObserver.disconnect()
      this._horizontalGalleryResizeObserver = null
    }
    this.horizontalRevealOnLoadAttached = false
  },
  methods: {
    /**
     * Horizontal-thumb Flickity often measures 0×0 when the AB wrapper is hidden
     * or before images/layout settle. Reflow after load and when the wrapper resizes.
     */
    reflowHorizontalFlickityInstances () {
      if (!this.isHorizontalThumbs) {
        return
      }
      const thumbs = this.$refs.thumbnailCarousel
      const main = this.$refs.mainCarousel
      if (!thumbs || !main) {
        return
      }
      const thumbsInst = typeof thumbs.flickity === 'function'
        ? thumbs.flickity()
        : null
      const mainInst = typeof main.flickity === 'function'
        ? main.flickity()
        : null
      if (!thumbsInst || !mainInst) {
        return
      }
      thumbs.reloadCells()
      thumbs.resize()
      thumbs.reposition()
      main.reloadCells()
      main.resize()
      main.reposition()
    },
    bindHorizontalGalleryLayoutFix () {
      const scheduleReflow = () => {
        this.$nextTick(() => {
          this.reflowHorizontalFlickityInstances()
        })
      }

      const wrapper = this.$el && this.$el.closest
        ? this.$el.closest('.product__gallery-wrapper')
        : null
      if (!wrapper || typeof ResizeObserver === 'undefined') {
        return
      }

      this._horizontalGalleryRoRafId = null
      this._horizontalGalleryResizeObserver = new ResizeObserver(() => {
        if (this._horizontalGalleryRoRafId != null) {
          return
        }
        this._horizontalGalleryRoRafId = window.requestAnimationFrame(() => {
          this._horizontalGalleryRoRafId = null
          scheduleReflow()
        })
      })
      this._horizontalGalleryResizeObserver.observe(wrapper)
    },
    /**
     * Reflow horizontal carousels then drop loading state after layout settles.
     */
    runHorizontalReflowAndReveal () {
      this.reflowHorizontalFlickityInstances()
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.reflowHorizontalFlickityInstances()
            this.$nextTick(() => {
              if (this.$el && this.$el.classList) {
                this.$el.classList.remove('product__gallery--loading')
              }
              this.horizontalGalleryBusy = false
            })
          })
        })
      })
    },
    /**
     * Horizontal thumbs: stay hidden until reflow; first visit waits for window load.
     */
    queueHorizontalGalleryReveal () {
      const startReveal = () => {
        this.horizontalGalleryFirstReveal = false
        this.horizontalRevealOnLoadAttached = false
        this.runHorizontalReflowAndReveal()
      }

      if (this.horizontalGalleryFirstReveal) {
        if (document.readyState === 'complete') {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(startReveal)
          })
          return
        }
        if (this.horizontalRevealOnLoadAttached) {
          return
        }
        this.horizontalRevealOnLoadAttached = true
        window.addEventListener('load', startReveal, { once: true })
        return
      }

      this.runHorizontalReflowAndReveal()
    },
    galleryLoad () {
      if (!this.$el || !this.$el.classList) {
        return
      }

      if (this.isHorizontalThumbs) {
        if (this.horizontalGalleryFirstReveal) {
          this.$el.classList.add('product__gallery--loading')
        }
      } else {
        this.$el.classList.remove('product__gallery--loading')
      }

      this.$refs.thumbnailCarousel.reloadCells()
      this.galleryDragWatcher()

      if (this.isHorizontalThumbs) {
        this.$nextTick(() => {
          this.reflowHorizontalFlickityInstances()
          this.queueHorizontalGalleryReveal()
        })
      }
    },
    refreshSliderPositions (newSlideIndex) {
      this.atFirstSlide = newSlideIndex === 0
      this.atLastSlide = newSlideIndex === (this.gallerySize - 1)
      this.currentSlideIndex = newSlideIndex
    },
    galleryDragWatcher () {
      this.$refs.mainCarousel.on('change', newSlideIndex => {
        if (!this.isHorizontalThumbs) {
          this.$refs.thumbnailCarousel.select(newSlideIndex)
          this.$refs.thumbnailCarousel.reloadCells()
        }
        this.refreshSliderPositions(newSlideIndex)
      })
    },
    previousSlide () {
      if (!this.isHorizontalThumbs) {
        this.$refs.thumbnailCarousel.previous()
      }
      this.$refs.mainCarousel.previous()
    },
    nextSlide () {
      if (!this.isHorizontalThumbs) {
        this.$refs.thumbnailCarousel.next()
      }
      this.$refs.mainCarousel.next()
    },
    goToSlide (index) {
      if (!this.isHorizontalThumbs) {
        this.$refs.thumbnailCarousel.select(index)
        this.$refs.thumbnailCarousel.reloadCells()
      }
      this.$refs.mainCarousel.select(index)
      this.$refs.mainCarousel.reloadCells()
      this.refreshSliderPositions(index)
    }
  }
}
</script>
